'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useDebounceFn, useLatest } from 'ahooks'
import { jwtDecode } from 'jwt-decode'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { createContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react'

import { routing } from '@/i18n/routing'
import { isJwtTokenExpired } from '@/lib/jwt-util'
import { deleteAuthJwtToken, deleteAuthSessionCookie } from '@/services/auth-session-client-service'
import { authSessionLogout, getAuthSession } from '@/services/auth-session-service'
import { Organization } from '@/types/organization'
import { UserJWT, UserSession } from '@/types/user'

interface AuthContextType {
  session: UserSession | null
  isLoading: boolean
  refreshSession: () => Promise<UserSession | null>
  refreshSessionIfExpired: () => Promise<{ isRefreshed: boolean; session: UserSession | null }>
  signOut: () => Promise<void>
  updateSessionOrganization: (_organization: Organization) => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<UserSession | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const queryClient = useQueryClient()
  const currentLocale = useLocale()

  // used to avoid having stale session variable inside the closure in the refresh session function
  const sessionRef = useLatest(session)
  const inFlightRefreshSessionPromise = useRef<Promise<UserSession | null> | null>(null)

  const { run: loadSession } = useDebounceFn(
    () => {
      setIsLoading(true)
      // Use setTimeout to ensure this runs after the current render cycle
      setTimeout(async () => {
        try {
          const sessionData = await getAuthSession()
          setSession(sessionData)
        } catch (error) {
          console.error('Failed to load session:', error)

          setSession(null)
        } finally {
          setIsLoading(false)
        }
      }, 0)
    },
    {
      wait: 250,
      maxWait: 10000,
    },
  )

  const syncLocaleWithOrg = useCallback(
    (orgLanguage: string | undefined) => {
      if (orgLanguage && orgLanguage !== currentLocale && routing.locales.includes(orgLanguage as any)) {
        // Redirect to correct locale - middleware will handle the rest
        const newPath = `/${orgLanguage}${pathname}`
        router.push(newPath)
      }
    },
    [currentLocale, pathname, router],
  )

  // since refs are used to get the latest values, no need to put them in dependency array
  const refreshSession = useCallback(async () => {
    console.info('Refreshing session')
    if (inFlightRefreshSessionPromise.current) {
      console.info('Session refresh already in progress')
      return inFlightRefreshSessionPromise.current
    }

    inFlightRefreshSessionPromise.current = (async () => {
      await deleteAuthJwtToken()
      return await getAuthSession({ forceLatest: true })
    })()

    try {
      const updatedSession = await inFlightRefreshSessionPromise.current
      setSession(updatedSession)

      // Sync locale with org language after session refresh
      syncLocaleWithOrg(updatedSession?.user?.activeOrganization?.language)
      return updatedSession
    } catch (error) {
      console.error('Failed to refresh session:', error)
      return sessionRef.current
    } finally {
      inFlightRefreshSessionPromise.current = null
    }
  }, [syncLocaleWithOrg])

  const refreshSessionIfExpired = useCallback(async () => {
    let decodedToken: UserJWT | undefined
    try {
      if (!sessionRef.current?.jwtToken) throw new Error('No jwtToken')
      decodedToken = jwtDecode<UserJWT>(sessionRef.current.jwtToken)
    } catch (e) {
      // invalid token, force refresh
      const newSession = await refreshSession()
      return { isRefreshed: true, session: newSession }
    }
    const isTokenExpired = isJwtTokenExpired(decodedToken)
    if (isTokenExpired) {
      const newSession = await refreshSession()
      return { isRefreshed: true, session: newSession }
    }
    return { isRefreshed: false, session: sessionRef.current }
  }, [refreshSession])

  const signOut = async () => {
    try {
      //  invalidate the session
      setSession(null)

      // cancel and remove all react query cache
      await queryClient.cancelQueries(undefined, { silent: true })
      queryClient.removeQueries()

      await authSessionLogout()
      await deleteAuthSessionCookie()
      router.push('/auth/login')
    } catch (error) {
      console.error('Failed to sign out:', error)
    }
  }

  const updateSessionOrganization = (organization: Organization) => {
    if (!sessionRef.current) return
    setSession((prev) => {
      if (!prev || !prev.user) return prev
      return {
        ...prev,
        user: {
          ...prev.user,
          activeOrganization: organization,
        },
      }
    })

    // Sync locale when org is updated
    syncLocaleWithOrg(organization.language)
  }

  // load session on pathname change
  useEffect(() => {
    loadSession()
  }, [pathname])

  // load session on component mount
  useEffect(() => {
    loadSession()
  }, [])

  return (
    <AuthContext.Provider
      value={{ session, isLoading, refreshSession, refreshSessionIfExpired, updateSessionOrganization, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default function AuthProviders({ children }: { children: ReactNode }) {
  return <AuthProvider>{children}</AuthProvider>
}
